import { ModuleLayout } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'

import { BalanceSideProvider, useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'
import { BalanceSide } from '@features/balanceLedger/enums/balanceSide'
import { LedgerEntryColumn } from '@features/balanceLedger/enums/ledgerEntryColumn'
import { useExpertReportPeriod } from '@features/expertReports'
import { TransactionViewProvider } from '@features/transactions/contexts/transactionViewContext'
import { TransactionViewTrackingContext } from '@features/transactions/enums/transactionViewTrackingContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { usePeriodDates } from '../../../../hooks/usePeriodDates'
import { PeriodValue } from '../../../../types/periodValue'
import { AccountLabel } from '../../elements/AccountLabel'
import { SuppliersBalanceEmptyState } from '../../elements/SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import { SuppliersBalancePeriodSelect } from '../../elements/SuppliersBalancePeriodSelect'
import type { ItemsPerPage } from '../../elements/TableFooter'
import { useFetchCreditorBalanceStatement } from '../../hooks/useFetchCreditorBalanceStatement'
import { useFetchCreditorBalanceSummary } from '../../hooks/useFetchCreditorBalanceSummary'
import { useLedgerEntriesFilters } from '../../hooks/useLedgerEntriesFilters'
import type { LedgerEntriesSortState } from '../../types/LedgerEntriesSortState'
import { buildSuppliersBalanceNavQuery } from '../../utils/buildSuppliersBalanceNavQuery'
import { LedgerEntriesTable } from './elements/LedgerEntriesTable'
import { SupplierListSidebar } from './elements/SupplierListSidebar'
import { useTrackSuppliersBalanceDetailView } from './hooks/useTrackSuppliersBalanceDetailView'
import { DetailColumn, DetailContent, Layout, SidebarColumn } from './styles'

const DEFAULT_ITEMS_PER_PAGE: ItemsPerPage = 25

export const SuppliersBalanceDetailViewContent = (): ReactElement => {
  const config = useBalanceConfig()
  const { bt } = useBalanceLabels()
  // The route is always matched with an :id segment, so this is never actually undefined.
  const { id } = useParams<{ id: string }>() as { id: string }
  const { organization } = useUserOrganization()
  const [{ ledgerSearchQuery }, setFilters] = useLedgerEntriesFilters()
  // Period is shared with the overview via the `period` URL query param; on direct access it defaults to the current fiscal year.
  const { encodedPeriod, periodType, setPeriod } = useExpertReportPeriod()
  const { toDate: periodEndDate } = usePeriodDates(encodedPeriod)
  // Carry the selected period back to the overview so it stays in sync across navigation (AUDIT-126).
  const { url: overviewUrl } = useOrganizationViewUrl(
    config.getMainRoute(),
    buildSuppliersBalanceNavQuery({ period: encodedPeriod }),
  )

  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPage>(DEFAULT_ITEMS_PER_PAGE)
  const [sortState, setSortState] = useState<LedgerEntriesSortState>()
  const hasActiveSearch = !!ledgerSearchQuery

  const handleSort = useCallback((columnId: LedgerEntryColumn, isDesc: boolean): void => {
    setSortState({ columnId, isDesc })
    setPage(1)
  }, [])

  const sortBy = sortState ? config.ledgerSortByMap[sortState.columnId] : undefined
  // Only send a direction when a sortBy resolves — a lone sortDirection is rejected by the API.
  let sortDirection: 'ASC' | 'DESC' | undefined
  if (sortState && sortBy) {
    sortDirection = sortState.isDesc ? 'DESC' : 'ASC'
  }

  const {
    data: suppliers,
    isLoading: isSidebarLoading,
    isFetchingNewPeriod: isSidebarFetchingNewPeriod,
  } = useFetchCreditorBalanceSummary({
    organizationId: organization?.id ?? '',
    period: encodedPeriod,
    sortBy: config.defaultSortBy,
    sortDirection: 'ASC',
  })

  const {
    entries: ledgerEntries,
    totalBalance,
    periodMovement,
    openingBalance,
    contact,
    pagination,
    hasStatement,
    isLoading,
    isFetchingNewParams,
    isError,
  } = useFetchCreditorBalanceStatement({
    organizationId: organization?.id ?? '',
    contactId: id,
    period: encodedPeriod,
    search: ledgerSearchQuery,
    page,
    pageSize: itemsPerPage,
    sortBy,
    sortDirection,
  })

  const resultsCount = pagination?.totalEntries ?? 0
  // isFetchingNewParams alone doesn't cover the initial fetch (no previous data for keepPreviousData to serve) or
  // an errored fetch with nothing cached, so tracking would otherwise settle on a false `resultsCount` of 0.
  const isResultsUnsettled = isLoading || isFetchingNewParams || isError
  const { armSearchTracking, trackBackClick } = useTrackSuppliersBalanceDetailView({
    accountId: id,
    isResultsUnsettled,
    periodType,
    resultsCount,
    searchQuery: ledgerSearchQuery,
  })

  const handlePeriodChange = useCallback(
    (value?: PeriodValue): boolean => {
      if (!value) {
        return false
      }

      return setPeriod(value)
    },
    [setPeriod],
  )

  // Changing the period clears any active ledger search and arms search tracking (AC3); pagination
  // resets via the effect below. Done as a reaction to encodedPeriod actually changing, not inside
  // handlePeriodChange, because period and search are two separate useQueryParams instances.
  // Clearing search right in the handler would read the URL before setPeriod's update lands there,
  // so it would undo the period change. Waiting for encodedPeriod to actually change avoids that (see BOOK-2569).
  const previousEncodedPeriodRef = useRef(encodedPeriod)
  useEffect(() => {
    if (previousEncodedPeriodRef.current !== encodedPeriod) {
      // An empty previous period means the fiscal-year default just resolved after the organization loaded,
      // not a user pick, so a search restored from the URL (or typed while loading) must survive it.
      const isDefaultPeriodHydration = !previousEncodedPeriodRef.current
      previousEncodedPeriodRef.current = encodedPeriod

      if (!isDefaultPeriodHydration) {
        setFilters({ ledgerSearchQuery: undefined })
        armSearchTracking(undefined)
      }
    }
  }, [encodedPeriod, setFilters, armSearchTracking])

  // Threaded down to `LedgerEntriesSearch` as a prop rather than having it call `useLedgerEntriesFilters` itself: a
  // second hook instance only syncs from the URL on the next render, which raced tracking against a stale
  // `isFetchingNewParams`/`resultsCount`. Also arms `Detail Search Used` only on real search interactions.
  const handleSearchChange = useCallback(
    (value?: string) => {
      setFilters({ ledgerSearchQuery: value })
      armSearchTracking(value)
    },
    [armSearchTracking, setFilters],
  )

  useEffect(() => {
    setPage(1)
  }, [encodedPeriod, ledgerSearchQuery, id])

  const handleItemsPerPageChange = (nextItemsPerPage: ItemsPerPage): void => {
    setItemsPerPage(nextItemsPerPage)
    setPage(1)
  }

  let title: ReactNode = bt('title')
  if (contact) {
    title = <AccountLabel accountNumber={contact.auxiliaryAccountNo} name={contact.contactName} />
  }

  const emptyState = isError ? (
    bt('error.ledger_load_failed')
  ) : (
    <SuppliersBalanceEmptyState text={ledgerSearchQuery ? bt('empty_state.no_entries_search') : undefined} />
  )

  const sideActions = <SuppliersBalancePeriodSelect encodedPeriod={encodedPeriod} onChange={handlePeriodChange} />

  return (
    <ModuleLayout
      fullHeight
      fullWidth
      title={title}
      sideActions={sideActions}
      withBackButton={!!overviewUrl}
      backButtonTo={overviewUrl}
      onBackButtonClick={trackBackClick}
    >
      <DetailContent>
        <Layout>
          <SidebarColumn fullHeight>
            <SupplierListSidebar
              suppliers={suppliers}
              isLoading={isSidebarLoading || isSidebarFetchingNewPeriod}
              period={encodedPeriod}
            />
          </SidebarColumn>
          <DetailColumn data-testid="suppliers-balance-detail-view">
            <LedgerEntriesTable
              data={ledgerEntries}
              emptyRowInfo={emptyState}
              hasActiveSearch={hasActiveSearch}
              isLoading={isLoading || isFetchingNewParams}
              ledgerSearchQuery={ledgerSearchQuery}
              onSearchChange={handleSearchChange}
              onSort={handleSort}
              openingBalance={openingBalance}
              pagination={{
                currentPage: page,
                itemsPerPage,
                totalPageItems: pagination?.totalEntries ?? 0,
                onPageChange: setPage,
                onItemsPerPageChange: handleItemsPerPageChange,
              }}
              queryKey={`${id}|${encodedPeriod}`}
              sortState={sortState}
              totals={
                hasStatement
                  ? {
                      closingBalance: totalBalance,
                      periodEndDate,
                      periodMovement,
                    }
                  : undefined
              }
            />
          </DetailColumn>
        </Layout>
      </DetailContent>
    </ModuleLayout>
  )
}

export const SuppliersBalanceDetailView = (): ReactElement => (
  <BalanceSideProvider side={BalanceSide.Supplier}>
    <TransactionViewProvider trackingContext={TransactionViewTrackingContext.SuppliersBalance}>
      <SuppliersBalanceDetailViewContent />
    </TransactionViewProvider>
  </BalanceSideProvider>
)
